import { useEffect, useState } from "react";
import { buildHostUrl } from "../utils/url";

/**
 * Tracks the host's currently-active telehealth meeting in localStorage so
 * a "Rejoin" affordance can be surfaced in the global NavBar — even after
 * the host closes the meeting overlay or refreshes the page. The entry
 * persists until the meeting is explicitly discarded (Cancel / No Show)
 * or completed.
 *
 * Cross-tab and same-tab listeners both fire, so opening the host link in
 * a second tab keeps the NavBar Rejoin button consistent everywhere.
 */
const STORAGE_KEY = "telehealth-v2:active-meeting";
const CHANGE_EVENT = "telehealth-v2:active-meeting-changed";

export interface ActiveMeeting {
  room: string;
  displayName: string;
  appointmentId?: string;
  patientId?: string;
  patientName?: string;
  startTime?: string;
}

function read(): ActiveMeeting | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.room !== "string") return undefined;
    return parsed as ActiveMeeting;
  } catch {
    return undefined;
  }
}

function notifyChanged() {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* ignore — feature degrades to per-mount reads only */
  }
}

export function setActiveMeeting(m: ActiveMeeting) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
  } catch {
    /* localStorage may be disabled — feature degrades */
  }
  notifyChanged();
}

export function clearActiveMeeting() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  notifyChanged();
}

export function useActiveMeeting(): ActiveMeeting | undefined {
  const [meeting, setMeeting] = useState<ActiveMeeting | undefined>(read);
  useEffect(() => {
    const refresh = () => setMeeting(read());
    window.addEventListener(CHANGE_EVENT, refresh);
    // Cross-tab updates: localStorage's `storage` event fires in OTHER
    // tabs when the value changes — that's how the Rejoin button shows
    // up in any other tab the provider has open.
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  return meeting;
}

export function buildActiveMeetingUrl(m: ActiveMeeting): string {
  return (
    window.location.origin +
    buildHostUrl({
      room: m.room,
      displayName: m.displayName,
      appointmentId: m.appointmentId,
      patientId: m.patientId,
      startTime: m.startTime,
    })
  );
}
